// Roster export module. Turns the currently-viewed roster (week or
// month) into CSV or a native Excel workbook. Mirrors the shape of
// ledgerExport.js and inventory exports so the DataExportButton wires
// up identically.
//
// Excel workbook contains three sheets so operators can pivot the same
// data three ways without touching a formula:
//   1. "Shifts"       one row per shift with dates, hours, cost, department
//   2. "Daily totals" one row per calendar day with shift count / hours / cost
//   3. "Departments"  FOH / BOH / Other totals for the range
//
// Everything is client-side: no backend endpoint, no extra roundtrip.
// Numeric columns stay numeric so filters, formulas, and pivot tables
// work the moment the file opens.

import { buildCsv, downloadCsv, stampedFilename } from "./csvExport";
import { buildWorksheet, downloadWorkbook } from "./xlsxExport";
import { computeShiftCost, isoDate, addDays, startOfMonth, endOfMonth } from "./labor";
import { departmentForRole, departmentLabel, groupShiftsByDepartment } from "./departments";
import { getCurrency } from "./prefs";

// Weekday label ("Mon", "Tue", ...) from an ISO date string. Uses UTC
// so an ISO date never drifts to a neighbouring day in exotic locales.
function weekdayShort(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });
}

// Builds the enriched shift row set used by both CSV and Excel exports.
// Rows are sorted by date, then by start_time, so the file reads like
// the printed schedule (Monday top, latest first inside a day is not
// what a chef wants: they want the earliest first).
function buildShiftRows(shifts, staffById) {
  const rows = shifts.map((sh) => {
    const staff = staffById[sh.staff_id];
    const c = computeShiftCost(sh, staffById);
    const dep = departmentForRole(staff?.role);
    return {
      date: sh.date,
      weekday: weekdayShort(sh.date),
      staff_name: staff?.name || "(unassigned)",
      role: staff?.role || "",
      department: dep,
      start: sh.start_time || "",
      end: sh.end_time || "",
      break_minutes: sh.break_minutes || 0,
      hours: c.hours,
      hourly_rate: c.rate,
      cost: c.cost,
      notes: sh.notes || "",
    };
  });
  rows.sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    if (a.start !== b.start) return a.start < b.start ? -1 : 1;
    return a.staff_name.localeCompare(b.staff_name);
  });
  return rows;
}

// Human-readable range label used as the CSV filename prefix and the
// footer in the Excel workbook. mode: "week" | "month".
function rangeLabel(mode, weekStart, monthAnchor) {
  if (mode === "week") {
    const end = addDays(weekStart, 6);
    return `roster_${isoDate(weekStart)}_to_${isoDate(end)}`;
  }
  return `roster_${isoDate(startOfMonth(monthAnchor))}_to_${isoDate(endOfMonth(monthAnchor))}`;
}

// CSV export: single flat table of every shift in the current view.
// Columns match the Excel "Shifts" sheet so an operator who wants a
// spreadsheet with formulas can jump to the xlsx variant without any
// header remap. Numeric columns are plain unformatted numbers so
// downstream tools can parse them.
export function exportRosterCsv({ shifts, staff, mode, weekStart, monthAnchor }) {
  const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
  const rows = buildShiftRows(shifts, staffById);
  const columns = [
    { label: "Date", get: (r) => r.date },
    { label: "Weekday", get: (r) => r.weekday },
    { label: "Staff", get: (r) => r.staff_name },
    { label: "Role", get: (r) => r.role },
    { label: "Department", get: (r) => r.department },
    { label: "Start", get: (r) => r.start },
    { label: "End", get: (r) => r.end },
    { label: "Break minutes", get: (r) => r.break_minutes },
    { label: "Hours", get: (r) => r.hours.toFixed(2) },
    { label: "Hourly rate", get: (r) => r.hourly_rate.toFixed(2) },
    { label: "Cost", get: (r) => r.cost.toFixed(2) },
    { label: "Notes", get: (r) => r.notes },
  ];
  const csv = buildCsv(rows, columns);
  const filename = stampedFilename(rangeLabel(mode, weekStart, monthAnchor), "csv");
  downloadCsv(filename, csv);
}

// Excel export: three sheets that give an operator the same picture
// they see on screen (per shift, per day, per department) without
// asking them to write a single formula.
export function exportRosterXlsx({ shifts, staff, mode, weekStart, monthAnchor, currency }) {
  const cur = currency || getCurrency() || "USD";
  const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
  const rows = buildShiftRows(shifts, staffById);

  // --- Sheet 1: Shifts ---
  const shiftColumns = [
    { label: "Date", get: (r) => r.date, type: "date" },
    { label: "Weekday", get: (r) => r.weekday },
    { label: "Staff", get: (r) => r.staff_name },
    { label: "Role", get: (r) => r.role },
    { label: "Department", get: (r) => r.department },
    { label: "Start", get: (r) => r.start },
    { label: "End", get: (r) => r.end },
    { label: "Break minutes", get: (r) => r.break_minutes, type: "integer" },
    { label: "Hours", get: (r) => r.hours, type: "number" },
    { label: "Hourly rate", get: (r) => r.hourly_rate, type: "money" },
    { label: "Cost", get: (r) => r.cost, type: "money" },
    { label: "Notes", get: (r) => r.notes },
  ];
  const sheets = [buildWorksheet(rows, shiftColumns, "Shifts", cur)];

  // --- Sheet 2: Daily totals ---
  const dayMap = new Map();
  for (const r of rows) {
    const bucket = dayMap.get(r.date) || {
      date: r.date,
      weekday: r.weekday,
      shifts: 0,
      hours: 0,
      cost: 0,
      people: new Set(),
    };
    bucket.shifts += 1;
    bucket.hours += r.hours;
    bucket.cost += r.cost;
    bucket.people.add(r.staff_name);
    dayMap.set(r.date, bucket);
  }
  const dailyRows = Array.from(dayMap.values())
    .map((b) => ({ ...b, people: b.people.size }))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  if (dailyRows.length > 0) {
    const dailyColumns = [
      { label: "Date", get: (r) => r.date, type: "date" },
      { label: "Weekday", get: (r) => r.weekday },
      { label: "Shifts", get: (r) => r.shifts, type: "integer" },
      { label: "People", get: (r) => r.people, type: "integer" },
      { label: "Hours", get: (r) => r.hours, type: "number" },
      { label: "Labor cost", get: (r) => r.cost, type: "money" },
    ];
    sheets.push(buildWorksheet(dailyRows, dailyColumns, "Daily totals", cur));
  }

  // --- Sheet 3: Departments (FOH / BOH / Other) ---
  const depTotals = groupShiftsByDepartment(shifts, staffById, computeShiftCost);
  const depRows = ["FOH", "BOH", "Other"]
    .map((code) => ({ code, ...depTotals[code] }))
    .filter((d) => d.shifts > 0)
    .map((d) => ({
      department: departmentLabel(d.code),
      code: d.code,
      people: d.staffIds.size,
      shifts: d.shifts,
      hours: d.hours,
      cost: d.cost,
    }));
  if (depRows.length > 0) {
    const depColumns = [
      { label: "Department", get: (r) => r.department },
      { label: "Code", get: (r) => r.code },
      { label: "People", get: (r) => r.people, type: "integer" },
      { label: "Shifts", get: (r) => r.shifts, type: "integer" },
      { label: "Hours", get: (r) => r.hours, type: "number" },
      { label: "Labor cost", get: (r) => r.cost, type: "money" },
    ];
    sheets.push(buildWorksheet(depRows, depColumns, "Departments", cur));
  }

  downloadWorkbook(sheets, rangeLabel(mode, weekStart, monthAnchor));
}
